/**
 * Service for mapping underlying symbols to their sectors
 * Provides efficient sector lookup with caching
 */

import { underlyingsApi } from "@/lib/api/underlyings";
import type { UnderlyingWithPreferences } from "@/types/underlyings";

interface SectorInfo {
  sector: string;
  userDefinedSector?: string;
  assetType: "EQUITY" | "ETF" | "INDEX" | "FUTURE";
  isETF: boolean;
}

class SectorMappingService {
  private cache = new Map<string, SectorInfo>();
  private batchFetchPromise: Promise<void> | null = null;
  private pendingSymbols = new Set<string>();
  private readonly CACHE_DURATION = 30 * 60 * 1000; // 30 minutes
  private cacheTimestamp = 0;
  // Track symbols not found to avoid repeated full fetches
  private negativeCache = new Map<string, number>();
  private readonly NOT_FOUND_TTL = 30 * 60 * 1000; // 30 minutes
  private readonly FULL_CATALOG_LIMIT = 20000;

  /**
   * Get sector information for a single symbol
   */
  async getSectorInfo(symbol: string): Promise<SectorInfo | null> {
    // Route through the batch path so single lookups share the same coalesced,
    // narrow fetch (and benefit from stampede dedupe).
    const map = await this.getSectorInfoBatch([symbol]);
    return map.get(symbol) ?? null;
  }

  /**
   * Get sector information for multiple symbols efficiently.
   *
   * Fetches ONLY the requested-but-uncached symbols (not the full 20k-row
   * catalog), and coalesces concurrent callers into a single shared request so
   * a page that asks for sectors from many components at mount triggers one
   * fetch instead of a stampede.
   */
  async getSectorInfoBatch(
    symbols: string[],
  ): Promise<Map<string, SectorInfo>> {
    const results = new Map<string, SectorInfo>();
    const fresh =
      this.cacheTimestamp > 0 &&
      Date.now() - this.cacheTimestamp < this.CACHE_DURATION;

    const needed: string[] = [];
    for (const symbol of symbols) {
      const cached = this.cache.get(symbol);
      if (cached && fresh) {
        results.set(symbol, cached);
        continue;
      }
      // Drop a stale entry so it gets refetched below.
      if (cached) this.cache.delete(symbol);
      // Skip symbols recently confirmed absent.
      const nfAt = this.negativeCache.get(symbol);
      if (nfAt && Date.now() - nfAt < this.NOT_FOUND_TTL) continue;
      needed.push(symbol);
    }

    // Coalesced load with a bounded retry for symbols that just missed an
    // in-flight batch window. Stops early if a round makes no progress (e.g. the
    // API call failed) so we never hammer a failing endpoint.
    let prevRemaining = Number.POSITIVE_INFINITY;
    let guard = 0;
    while (guard++ < 5) {
      const stillNeeded = needed.filter(
        (s) => !this.cache.get(s) && !this.negativeCache.get(s),
      );
      if (stillNeeded.length === 0) break;
      if (stillNeeded.length >= prevRemaining) break; // no progress — stop
      prevRemaining = stillNeeded.length;
      stillNeeded.forEach((s) => this.pendingSymbols.add(s));
      await this.loadPending();
    }

    for (const symbol of needed) {
      const info = this.cache.get(symbol);
      if (info) results.set(symbol, info);
    }
    return results;
  }

  /**
   * Load all currently-pending symbols in one narrow request, shared across all
   * concurrent callers. Yields once before snapshotting `pendingSymbols` so
   * callers firing in the same tick all join this single batch.
   */
  private loadPending(): Promise<void> {
    if (this.batchFetchPromise) return this.batchFetchPromise;

    this.batchFetchPromise = (async () => {
      // Let all same-tick callers register their symbols before we snapshot.
      await Promise.resolve();
      const toFetch = Array.from(this.pendingSymbols);
      this.pendingSymbols.clear();
      if (toFetch.length === 0) return;

      try {
        const rows = await underlyingsApi.getAll({
          symbols: toFetch,
          isActive: true,
        });

        this.cacheTimestamp = Date.now();
        const found = new Set<string>();
        for (const underlying of rows) {
          this.cache.set(
            underlying.symbol,
            this.mapUnderlyingToSectorInfo(underlying),
          );
          found.add(underlying.symbol);
        }

        // Symbols asked for but not returned are absent — negative-cache them.
        const now = Date.now();
        for (const symbol of toFetch) {
          if (!found.has(symbol)) this.negativeCache.set(symbol, now);
        }
      } catch (error) {
        if (process.env.DEBUG_SECTOR_MAPPING === "true") {
          console.warn(
            "Sector Mapping: narrow lookup failed:",
            error instanceof Error ? error.message : String(error),
          );
        }
        // Leave symbols unresolved; the bounded retry loop gives up after this.
      } finally {
        this.batchFetchPromise = null;
      }
    })();

    return this.batchFetchPromise;
  }

  /**
   * Map underlying data to sector information
   */
  private mapUnderlyingToSectorInfo(
    underlying: UnderlyingWithPreferences,
  ): SectorInfo {
    // Priority: user preference > admin-defined sector > default sector
    const effectiveSector =
      underlying.userPreferences?.customSector ||
      underlying.userDefinedSector ||
      underlying.sector ||
      "Uncategorized";

    const normalizedSector = this.normalizeSector(effectiveSector);
    const allocationSector = this.resolveAllocationSector(
      underlying,
      normalizedSector,
    );
    const assetType =
      underlying.recommendationUniverse?.endsWith("_etf") ||
      underlying.recommendationUniverse === "broad_index_etf"
        ? "ETF"
        : underlying.assetType;

    // Log for debugging specific symbols
    if (underlying.symbol === "BULL" || underlying.symbol === "PYPL") {
      console.log(
        `Sector Mapping: mapUnderlyingToSectorInfo for ${underlying.symbol}:`,
        {
          sector: underlying.sector,
          userDefinedSector: underlying.userDefinedSector,
          userPreferencesCustomSector: underlying.userPreferences?.customSector,
          effectiveSector: effectiveSector,
          normalized: normalizedSector,
          allocationSector,
        },
      );
    }

    return {
      sector: allocationSector,
      userDefinedSector: underlying.userDefinedSector,
      assetType,
      isETF: assetType === "ETF",
    };
  }

  private resolveAllocationSector(
    underlying: UnderlyingWithPreferences,
    normalizedSector: string,
  ): string {
    if (normalizedSector !== "Other" && normalizedSector !== "Uncategorized") {
      return normalizedSector;
    }

    switch (underlying.recommendationUniverse) {
      case "broad_index_etf":
        return "Index ETFs";
      case "sector_etf":
        return "Sector ETFs";
      case "macro_etf":
        return this.resolveMacroEtfSector(underlying);
      default:
        return normalizedSector;
    }
  }

  private resolveMacroEtfSector(underlying: UnderlyingWithPreferences): string {
    const symbol = underlying.symbol.toUpperCase();
    const name = underlying.name.toUpperCase();

    if (
      ["IBIT", "BITO", "FBTC", "GBTC", "ETHE"].includes(symbol) ||
      name.includes("BITCOIN") ||
      name.includes("ETHER")
    ) {
      return "Crypto";
    }

    if (
      ["TLT", "IEF", "SHY", "HYG", "LQD", "BND"].includes(symbol) ||
      name.includes("TREASURY") ||
      name.includes("BOND")
    ) {
      return "Fixed Income";
    }

    if (
      ["GLD", "SLV", "USO", "UNG", "CORN", "SOYB", "WEAT"].includes(
        symbol,
      ) ||
      name.includes("GOLD") ||
      name.includes("SILVER") ||
      name.includes("OIL") ||
      name.includes("GAS")
    ) {
      return "Commodities";
    }

    if (name.includes("CURRENCY") || name.includes("EURO")) {
      return "Currencies";
    }

    return "Macro ETFs";
  }

  /**
   * Normalize sector names for consistency
   */
  private normalizeSector(sector: string): string {
    // Map common variations to standard names
    const sectorMap: Record<string, string> = {
      Tech: "Technology",
      Financial: "Financials",
      "Financial Services": "Financials",
      "Health Care": "Healthcare",
      "Consumer Cyclical": "Consumer Discretionary",
      "Consumer Defensive": "Consumer Staples",
      Communication: "Communication Services",
      "Comm Services": "Communication Services",
      REIT: "Real Estate",
      REITs: "Real Estate",
      "Basic Materials": "Materials",
      Index: "Index ETFs",
      "Sector ETF": "Sector ETFs",
      Commodity: "Commodities",
      Currency: "Currencies",
      Bond: "Fixed Income",
      Bonds: "Fixed Income",
      "Volatility Index": "Volatility",
      Cryptocurrency: "Crypto",
    };

    return sectorMap[sector] || sector;
  }

  /**
   * Clear the cache
   */
  clearCache(): void {
    this.cache.clear();
    this.negativeCache.clear();
    this.cacheTimestamp = 0;
  }

  /**
   * Force refresh all sector data from the API
   */
  async forceRefresh(): Promise<void> {
    console.log("Sector Mapping: Starting forceRefresh...");
    console.log(`Sector Mapping: Cache size before clear: ${this.cache.size}`);

    this.clearCache();

    console.log(`Sector Mapping: Cache size after clear: ${this.cache.size}`);
    console.log(
      `Sector Mapping: Cache timestamp after clear: ${this.cacheTimestamp}`,
    );

    try {
      // Fetch all underlyings to refresh cache
      console.log("Sector Mapping: Fetching all underlyings from API...");
      const allUnderlyings = await underlyingsApi.getAll({
        limit: this.FULL_CATALOG_LIMIT,
        isActive: true,
      });

      console.log(
        `Sector Mapping: Received ${allUnderlyings.length} underlyings from API`,
      );

      // Update cache with fresh data
      this.cacheTimestamp = Date.now();

      // Look for specific symbols we're interested in
      const bullUnderlying = allUnderlyings.find((u) => u.symbol === "BULL");
      const pyplUnderlying = allUnderlyings.find((u) => u.symbol === "PYPL");

      if (bullUnderlying) {
        console.log(`Sector Mapping: BULL found in API response:`, {
          symbol: bullUnderlying.symbol,
          sector: bullUnderlying.sector,
          userDefinedSector: bullUnderlying.userDefinedSector,
          assetType: bullUnderlying.assetType,
        });
      } else {
        console.log("Sector Mapping: BULL not found in API response");
      }

      if (pyplUnderlying) {
        console.log(`Sector Mapping: PYPL found in API response:`, {
          symbol: pyplUnderlying.symbol,
          sector: pyplUnderlying.sector,
          userDefinedSector: pyplUnderlying.userDefinedSector,
          assetType: pyplUnderlying.assetType,
        });
      } else {
        console.log("Sector Mapping: PYPL not found in API response");
      }

      for (const underlying of allUnderlyings) {
        const sectorInfo = this.mapUnderlyingToSectorInfo(underlying);
        this.cache.set(underlying.symbol, sectorInfo);

        // Log specific symbols
        if (underlying.symbol === "BULL" || underlying.symbol === "PYPL") {
          console.log(
            `Sector Mapping: Cached ${underlying.symbol} ->`,
            sectorInfo,
          );
        }
      }

      console.log(
        `Sector Mapping Service: Refreshed ${allUnderlyings.length} sector mappings`,
      );
      console.log(
        `Sector Mapping: Cache size after refresh: ${this.cache.size}`,
      );
      console.log(
        `Sector Mapping: New cache timestamp: ${this.cacheTimestamp}`,
      );
    } catch (error) {
      console.error("Failed to refresh sector mappings:", error);
      throw error;
    }
  }

  /**
   * Get sector classification (Equity vs Non-Equity)
   */
  getSectorClassification(sector: string): "equity" | "non-equity" {
    const nonEquitySectors = new Set([
      "Commodities",
      "Currencies",
      "Fixed Income",
      "Volatility",
      "Crypto",
      "Index ETFs",
      "Sector ETFs",
      "Macro ETFs",
    ]);

    return nonEquitySectors.has(sector) ? "non-equity" : "equity";
  }
}

// Export singleton instance
export const sectorMappingService = new SectorMappingService();
